import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { ConfigProvider, notification } from 'antd';
import { Notification, Text } from 'farmhub-ui-core';
import { createBrowserHistory } from 'history';
import queryString from 'query-string';
import {
  BrowserRouter,
  Route,
  Switch,
  Router as MainRouter,
} from 'react-router-dom';
import styled, { ThemeProvider } from 'styled-components';

import { EntityApi } from 'api';
import { ConfirmModal } from 'components';
import { App } from 'containers/App';
import { AccountManagementPage } from 'containers/App/Account/AccountManagement/Loadable';
import { WithSelected } from 'containers/App/Account/components';
import { AccountDetailPage } from 'containers/App/Account/DetailPage/Loadable';
import { AccountListPage } from 'containers/App/Account/ListPage/Loadable';
import { WithAuthenticated } from 'containers/App/Auth/components';
import { CollaboratorCreatePage } from 'containers/Branding/Collaborator/Collaborator/CreatePage/Loadable';
import { FarmCreatePage } from 'containers/Branding/Farm/Farm/CreatePage/Loadable';
import { StoreCreatePage } from 'containers/Branding/Store/Store/CreatePage/Loadable';
import GetSeo from 'hooks/GetSeo';
import { selectAccount, updateDomainNotFound } from 'slice/accountSlice';
import { theme } from 'styled';
import { REFRESH_TOKEN, SELECTED_ENTITY, USER_ID } from 'types';

import { useAppDispatch, useAppSelector } from './app';
import LoadingChangeAccountModal from './components/Modal/LoadingChangeAccountModal';
import {
  findOneUser,
  findProductUnitPostfix,
  findProductUnitPrefix,
  initialAppState,
  initialDevice,
  selectAppDevice,
  selectAppState,
  setIsSpecificBrowser,
  updateCanInit,
} from './slice';
import {
  DEFAULT_SLOGAN,
  DEFAULT_WEBSITE_NAME,
  detectIncognito,
  DEVICES,
  getAppTypeByDomain,
  PRIMARY_COLOR,
  removeAuthRouter,
} from './utils';

const AppWithAuthAdminFarm = WithAuthenticated(WithSelected(App));
const AccountListPageWithAuth = WithAuthenticated(AccountListPage);
const AccountDetailPageWithAuth = WithAuthenticated(AccountDetailPage);
const AccountManagementPageWithAuth = WithAuthenticated(AccountManagementPage);

const FarmCreatePageWithAuth = WithAuthenticated(FarmCreatePage);
const StoreCreatePageWithAuth = WithAuthenticated(StoreCreatePage);
const CollaboratorCreatePageWithAuth = WithAuthenticated(
  CollaboratorCreatePage
);

export const Router = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const [deviceTheme, setDeviceTheme] = useState(theme);
  const appDevice = useAppSelector(selectAppDevice);
  const app = useAppSelector(selectAppState);
  const user = useAppSelector(state => state.auth.item);
  const auth = useAppSelector(state => state.auth);
  const error = useAppSelector(state => state.error);
  const account = useAppSelector(selectAccount);
  const [timer, setTimer] = useState<any>(10);
  const intervalId = useRef<any>(null);

  const history = createBrowserHistory({ basename: '/admin' });

  const [height, setHeight] = useState<string>('calc(100vh - 0px)');
  const [isVisibleConfirmModal, setIsVisibleConfirmModal] = useState(false);

  useLayoutEffect(() => {
    const initTheme = () => {
      const vh = window.innerHeight;
      setHeight(`${vh - 0}px`);
    };

    initTheme();
  }, []);

  useEffect(() => {
    if (appDevice.isFirstLoad) {
      if (window.innerWidth <= 768) {
        dispatch(initialDevice(DEVICES.MOBILE));
      } else {
        dispatch(initialDevice(DEVICES.DESKTOP));
      }
    }

    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
  }, []);

  useEffect(() => {
    window.addEventListener('resize', () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    });

    return () => {
      window.removeEventListener('resize', () => false);
    };
  });

  useEffect(() => {
    const windowHeight = (): void => {
      const doc = document.documentElement;
      doc.style.setProperty('--window-height', `${window.innerHeight}px`);
    };
    window.addEventListener('resize', windowHeight);
    windowHeight();
  }, [window.innerHeight]);

  useEffect(() => {
    const getProductUnitPrefixList = async (): Promise<void> => {
      try {
        dispatch(findProductUnitPrefix()).unwrap();
      } catch (error: any) {
        Notification('error', 'Tải dữ liệu thất bại', error);
      }
    };
    const getProductUnitPostfixList = async (): Promise<void> => {
      try {
        dispatch(findProductUnitPostfix()).unwrap();
      } catch (error: any) {
        Notification('error', 'Tải dữ liệu thất bại', error);
      }
    };
    getProductUnitPrefixList();
    getProductUnitPostfixList();
  }, [dispatch]);

  useEffect(() => {
    const initUserData = async (userID: string): Promise<void> => {
      try {
        dispatch(findOneUser(userID)).unwrap();
      } catch (error: any) {
        console.log('An error occurred while finding user entity', error);
      }
    };

    if (user?.id) {
      initUserData(user.id);
    }
  }, [dispatch, user?.id]);

  useEffect(() => {
    notification.config({
      placement: 'bottomLeft',
      bottom: 0,
      rtl: false,
    });
  }, []);

  useEffect(() => {
    if (!history.location.search.includes('refresh=')) {
      if (!auth.canInit) {
        setTimeout(() => {
          dispatch(updateCanInit(true));
        }, 100);
      }
    } else {
      const query = queryString.parse(location.search);
      if (history.location.search.includes('refresh=')) {
        localStorage.removeItem(REFRESH_TOKEN);
        localStorage.setItem(REFRESH_TOKEN, query.refresh);
        // // retrieve all cookies
        // const cookies = document.cookie.split(';');
        // // set past expiry to all cookies
        // for (let i = 0; i < cookies.length; i++) {
        //   document.cookie = `${cookies[i]}=; Max-Age=0; path=/; domain=.identity-v2.stag.hubhub.asia`;
        // }
        // document.cookie = `userRefreshToken=${query.refresh}; Max-Age=36000; path=/; domain=.identity-v2.stag.hubhub.asia`;
      }

      if (history.location.search.includes('userID=')) {
        localStorage.removeItem(USER_ID);
        localStorage.setItem(USER_ID, query.userID);
      }

      if (history.location.search.includes('entity=')) {
        localStorage.removeItem(SELECTED_ENTITY);
        localStorage.setItem(SELECTED_ENTITY, query.entity);
      }

      if (!history.location.search.includes('productObjectID=')) {
        removeAuthRouter(history);
      }

      if (!auth.canInit) {
        setTimeout(() => {
          dispatch(updateCanInit(true));
        }, 100);
      }
    }
  }, [history?.location]);

  const initTheme = (system: any) => {
    const colorStore = system?.theme?.color ?? PRIMARY_COLOR;
    const customTheme = {
      ...theme,
    };

    customTheme.colors.primary.main = colorStore;
    customTheme.colors.text.main = colorStore;
    setDeviceTheme({ ...customTheme });

    ConfigProvider.config({
      theme: {
        primaryColor: colorStore,
      },
    });

    document.documentElement.style.setProperty(
      '--adm-color-primary',
      colorStore
    );
    document.documentElement.style.setProperty('--primary-pri-500', colorStore);
    document.documentElement.style.setProperty('--primary-pri-300', colorStore);
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
  };

  const handleGetDomain = async () => {
    try {
      let response;
      if (process.env.NODE_ENV === 'production') {
        response = await EntityApi.findFollowDomain({
          domain: `${window.location.origin}/admin`,
          appType: getAppTypeByDomain(),
        });
      } else {
        response = await EntityApi.findFollowDomain({
          domain: `https://supply.demo.ecovation.vn/admin`,
          appType: getAppTypeByDomain(),
        });
      }

      const data = response?.data;
      initTheme(data.userCustomDomain);
      dispatch(initialAppState(data));
      console.log(data);
    } catch (error: any) {
      if (error?.message == 'Request failed with status code 403') {
        dispatch(updateDomainNotFound(true));
      }
    }
  };

  useEffect(() => {
    const checkSpecificBrowser = async () => {
      if (window) {
        const result = await detectIncognito();
        if (result.isPrivate) {
          dispatch(setIsSpecificBrowser());
        }

        if (
          ((window.navigator.userAgent
            .toString()
            .toLocaleLowerCase()
            .includes('zalo') ||
            window.navigator.userAgent
              .toString()
              .toLocaleLowerCase()
              .includes('skype') ||
            window.navigator.userAgent
              .toString()
              .toLocaleLowerCase()
              .includes('fb')) &&
            appDevice.isFirstLoad) ||
          (!appDevice.isFirstLoad && appDevice.device !== DEVICES.DESKTOP) ||
          navigator.userAgent.indexOf('Safari') > -1
        ) {
          dispatch(setIsSpecificBrowser());
        }
      }
    };

    handleGetDomain();
    checkSpecificBrowser();
  }, []);

  const getSEO = useMemo(() => {
    return {
      title: `${app?.system?.seo?.title ?? DEFAULT_SLOGAN} | ${
        app?.system?.seo?.displayName ??
        app?.system?.displayName ??
        DEFAULT_WEBSITE_NAME
      }`,
      description: app?.system?.seo?.description
        ? app?.system?.seo?.description
        : app?.system?.description ?? '',
      canonicalUrl: `${app?.system?.domain ?? ''}`,
      image: app?.system?.seo?.banner
        ? app?.system?.seo?.banner
        : app?.system?.banner ?? '',
      icon: app?.system?.seo?.icon ?? '',
      brand: app?.system?.displayName ?? '',
      ratingValue: app?.system?.avgRating ?? 0,
      reviewCount: app?.system?.totalRating ?? 0,
      site: app?.system?.domain ?? '',
      siteName: app?.system?.displayName ?? '',
    };
  }, [app?.system]);

  const handleForbidden = () => {
    history.push('/account/list');
    localStorage.removeItem(SELECTED_ENTITY);
    window.location.reload();
  };

  const handleCountdown = useCallback(() => {
    intervalId.current = setInterval(() => {
      setTimer((timer: number) => timer - 1);
    }, 1000);
  }, [timer]);

  useEffect(() => {
    if (timer <= 0) {
      clearInterval(intervalId.current);
      handleForbidden();
    }
  }, [timer]);

  useEffect(() => {
    if (
      error.hasError &&
      (error?.message?.statusCode == 422 ||
        error?.message?.statusCode == 403) &&
      !isVisibleConfirmModal &&
      appDevice.device == DEVICES.DESKTOP
    ) {
      setIsVisibleConfirmModal(true);
      handleCountdown();
    }
  }, [error.hasError, error.message, appDevice.device]);

  return (
    <ThemeProvider theme={deviceTheme}>
      <GetSeo data={getSEO} />
      <Wrapper height={height}>
        <MainRouter history={history}>
          <BrowserRouter basename="/admin">
            {!(!auth.isAuthenticated && auth.isInitAuth) &&
              !account.isDomainNotFound && <LoadingChangeAccountModal />}
            <ConfirmModal
              hideIconClose
              isVisible={isVisibleConfirmModal}
              message={
                <Text styles={{ color: '#59636E' }}>
                  Bạn không có quyền{' '}
                  <Text color="main" weight="medium">
                    {account?.roleTitle}
                  </Text>{' '}
                  truy cập vào{' '}
                  <Text color="main" weight="medium">
                    {account?.item?.displayName}
                  </Text>
                  . Hệ thống tự động điều hướng trong{' '}
                  <Text
                    color="error"
                    styles={{ color: theme.colors.error.main }}
                    weight="bold"
                  >
                    {timer}s
                  </Text>
                  .
                </Text>
              }
              okText="Xác nhận"
              setIsVisible={setIsVisibleConfirmModal}
              title="Không có quyền truy cập"
              onOke={handleForbidden}
            />

            <Switch>
              <Route
                exact
                component={AccountListPageWithAuth}
                path="/account/template"
              />

              <Route
                exact
                component={AccountDetailPageWithAuth}
                path="/account/list"
              />

              <Route
                component={AccountManagementPageWithAuth}
                path="/account"
              />

              <Route
                exact
                component={FarmCreatePageWithAuth}
                path="/farm/create"
              />
              <Route
                exact
                component={StoreCreatePageWithAuth}
                path="/store/create"
              />
              <Route
                exact
                component={CollaboratorCreatePageWithAuth}
                path="/collaborator/create"
              />
              <Route component={AppWithAuthAdminFarm} path="/" />
            </Switch>
          </BrowserRouter>
        </MainRouter>
      </Wrapper>
    </ThemeProvider>
  );
};

const Wrapper = styled.div<{ height: string }>`
  width: 100%;
  min-height: ${props => props.height};
  height: ${props => props.height};
`;
